import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import { Box, Button, ConfirmationModal, IconButton, Row, TextField, Typography } from 'seagull';
import { Search } from 'seagull/icons';
import { ChevronLeft } from 'seagull/icons/arrows';
import { getTestIds } from 'seagull/utils/TestIds';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useFilePicker } from '~/hooks/useFilePicker';
import { useLocaleKeys } from '~/i18n';
import { useShowSelectCustomPageTypeModal } from '../../SelectCustomPageTypeModal/SelectCustomPageTypeModal';
import { CustomPageTemplateRow } from '../CustomPageTemplateRow/CustomPageTemplateRow';
import { PdfTemplateForm } from '../PdfTemplateFormModal/PdfTemplateFormModal';
import { TextTemplateForm } from '../TextTemplateFormModal/TextTemplateFormModal';
import { VideoTemplateForm } from '../VideoTemplateFormModal/VideoTemplateFormModal';
import s from './CustomPageTemplatesModal.module.scss';
export const GetCustomPageTemplatesModalTID = (parentTestId)=>getTestIds(parentTestId);
export const CustomPageTemplatesModal = (param)=>{
    let { dismissModal } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetCustomPageTemplatesModalTID(), []);
    const { templates } = useCustomPageTemplates({
        refreshOnHookMount: true
    });
    const showSelectTypeModal = useShowSelectCustomPageTypeModal();
    const [searchQuery, setSearchQuery] = useState('');
    const [view, setView] = useState({
        type: 'list'
    });
    const filtered = useMemo(()=>{
        const q = searchQuery.trim().toLocaleLowerCase();
        if (q === '') return templates;
        return templates.filter((tmpl)=>tmpl.name.toLocaleLowerCase().includes(q));
    }, [
        templates,
        searchQuery
    ]);
    const handleSearchChange = (e)=>{
        setSearchQuery(e.target.value);
    };
    const goToList = useCallback(()=>setView({
            type: 'list'
        }), []);
    const { openFilePicker: openPdfFilePicker, fileInputElement: pdfFileInputElement } = useFilePicker({
        accept: '.pdf',
        onFileSelected: (file)=>{
            setView({
                type: 'create',
                pageType: 'pdf',
                initialFile: file
            });
        }
    });
    const handleCreateNew = ()=>{
        showSelectTypeModal((type)=>{
            if (type === 'pdf') {
                openPdfFilePicker();
            } else {
                setView({
                    type: 'create',
                    pageType: type
                });
            }
        });
    };
    const handleEdit = useCallback((template)=>{
        setView({
            type: 'edit',
            template
        });
    }, []);
    const isFormView = view.type !== 'list';
    const title = isFormView ? view.type === 'create' ? t.customPages.templates.create() : t.customPages.templates.edit() : t.customPages.templates.title();
    const renderListContent = ()=>{
        return /*#__PURE__*/ _jsxs(Box, {
            className: s.bodyContent,
            children: [
                templates.length > 0 && /*#__PURE__*/ _jsx(Box, {
                    className: s.searchWrapper,
                    children: /*#__PURE__*/ _jsx(TextField, {
                        testId: testIds.CustomPageTemplatesModalTestIds.SearchField(),
                        placeholder: t.customPages.templates.searchPlaceholder(),
                        value: searchQuery,
                        onChange: handleSearchChange,
                        prefix: /*#__PURE__*/ _jsx(Search, {
                            size: 20
                        }),
                        bi: {
                            name: 'custom_page_templates_search'
                        }
                    })
                }),
                templates.length === 0 ? /*#__PURE__*/ _jsx(Box, {
                    testId: testIds.CustomPageTemplatesModalTestIds.EmptyState(),
                    className: s.emptyState,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        colorToken: "neutrals-700",
                        children: t.customPages.templates.emptyState()
                    })
                }) : filtered.length === 0 ? /*#__PURE__*/ _jsx(Box, {
                    testId: testIds.CustomPageTemplatesModalTestIds.NoResults(),
                    className: s.noResults,
                    children: /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        colorToken: "neutrals-700",
                        children: t.customPages.templates.noResults()
                    })
                }) : /*#__PURE__*/ _jsx(Box, {
                    testId: testIds.CustomPageTemplatesModalTestIds.TemplateList(),
                    className: s.templateList,
                    children: filtered.map((tmpl)=>/*#__PURE__*/ _jsx(CustomPageTemplateRow, {
                            template: tmpl,
                            onEdit: handleEdit
                        }, tmpl.id))
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    className: s.listFooter,
                    justifyContent: "center",
                    gap: 12,
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            skin: "primaryOutline",
                            onClick: dismissModal,
                            testId: testIds.CustomPageTemplatesModalTestIds.ListCancelButton(),
                            bi: {
                                name: 'custom_page_templates_close'
                            },
                            children: t.customPages.templates.cancel()
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            skin: "primary",
                            onClick: handleCreateNew,
                            testId: testIds.CustomPageTemplatesModalTestIds.ListAddNewButton(),
                            bi: {
                                name: 'custom_page_templates_create_new'
                            },
                            children: t.customPages.addNew()
                        })
                    ]
                })
            ]
        });
    };
    const renderFormContent = ()=>{
        const commonProps = {
            onBack: goToList,
            onSaved: goToList
        };
        if (view.type === 'create') {
            switch(view.pageType){
                case 'video':
                    return /*#__PURE__*/ _jsx(VideoTemplateForm, _object_spread({
                        mode: "create"
                    }, commonProps));
                case 'pdf':
                    return /*#__PURE__*/ _jsx(PdfTemplateForm, _object_spread({
                        mode: "create",
                        initialFile: view.initialFile
                    }, commonProps));
                case 'text':
                    return /*#__PURE__*/ _jsx(TextTemplateForm, _object_spread({
                        mode: "create"
                    }, commonProps));
            }
        }
        if (view.type === 'edit') {
            switch(view.template.type){
                case 'video':
                    return /*#__PURE__*/ _jsx(VideoTemplateForm, _object_spread({
                        mode: "edit",
                        template: view.template
                    }, commonProps));
                case 'pdf':
                    return /*#__PURE__*/ _jsx(PdfTemplateForm, _object_spread({
                        mode: "edit",
                        template: view.template
                    }, commonProps));
                case 'text':
                    return /*#__PURE__*/ _jsx(TextTemplateForm, _object_spread({
                        mode: "edit",
                        template: view.template
                    }, commonProps));
            }
        }
        return null;
    };
    return /*#__PURE__*/ _jsxs(Box, {
        className: s.modalWrapper,
        children: [
            pdfFileInputElement,
            isFormView && /*#__PURE__*/ _jsx(IconButton, {
                skin: "transparent",
                size: "smallMedium",
                onClick: goToList,
                testId: testIds.CustomPageTemplatesModalTestIds.BackButton(),
                bi: {
                    name: 'custom_page_templates_back'
                },
                className: s.backButton,
                children: /*#__PURE__*/ _jsx(ChevronLeft, {
                    size: 20
                })
            }),
            /*#__PURE__*/ _jsx(ConfirmationModal, {
                testId: testIds.CustomPageTemplatesModalTestIds.Root(),
                title: title,
                onCloseButtonClick: dismissModal,
                width: "800px",
                textAlignCenter: false,
                contentClassName: s.contentArea,
                content: /*#__PURE__*/ _jsxs(Box, {
                    className: s.slideContainer,
                    children: [
                        /*#__PURE__*/ _jsx(Box, {
                            className: cn(s.slidePanel, {
                                [s.slideOut]: isFormView
                            }),
                            children: renderListContent()
                        }),
                        /*#__PURE__*/ _jsx(Box, {
                            className: cn(s.slidePanel, s.formPanel, {
                                [s.slideIn]: isFormView
                            }),
                            children: isFormView && renderFormContent()
                        })
                    ]
                }),
                bi: {
                    primaryButton: {
                        name: 'custom_page_templates_create_new'
                    },
                    secondaryButton: {
                        name: 'custom_page_templates_close'
                    },
                    closeButton: {
                        name: 'custom_page_templates_close_x'
                    }
                }
            })
        ]
    });
};
